import { useState, useCallback } from 'react';

/**
 * Custom hook for handling form state and validation
 * @param {Object} initialValues - Initial form values
 * @param {Function} validateForm - Function to validate form values
 * @param {Function} onSubmit - Function to handle form submission
 * @returns {Object} - Form handling utilities
 */
const useForm = (initialValues = {}, validateForm = () => ({}), onSubmit = () => {}) => {
  // Form values state
  const [values, setValues] = useState(initialValues);
  
  // Form errors state
  const [errors, setErrors] = useState({});
  
  // Form touched fields state
  const [touched, setTouched] = useState({});
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form submission success state
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  /**
   * Handle input change
   * @param {Event} e - Change event
   */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle different input types
    const inputValue = type === 'checkbox' ? checked : value;
    
    setValues((prevValues) => ({
      ...prevValues,
      [name]: inputValue
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null
      }));
    }
  }, [errors]);
  
  /**
   * Handle input blur (mark field as touched)
   * @param {Event} e - Blur event
   */
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    
    setTouched((prevTouched) => ({
      ...prevTouched,
      [name]: true
    }));
    
    // Validate single field on blur
    const fieldErrors = validateForm({
      ...values,
      [name]: values[name]
    });
    
    setErrors((prevErrors) => ({
      ...prevErrors,
      ...fieldErrors
    }));
  }, [validateForm, values]);
  
  /**
   * Set a specific form value programmatically
   * @param {string} name - Field name
   * @param {any} value - Field value
   */
  const setFieldValue = useCallback((name, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value
    }));
  }, []);
  
  /**
   * Reset form to initial values
   */
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
    setIsSubmitted(false);
  }, [initialValues]);
  
  /**
   * Handle form submission
   * @param {Event} e - Submit event
   */
  const handleSubmit = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    // Validate all fields
    const formErrors = validateForm(values);
    setErrors(formErrors);
    
    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    // Check if there are any errors
    if (Object.keys(formErrors).length === 0) {
      setIsSubmitting(true);
      
      try {
        await onSubmit(values);
        setIsSubmitted(true);
      } catch (error) {
        // Handle submission error
        setErrors((prevErrors) => ({
          ...prevErrors,
          submit: error.message || 'Form submission failed'
        }));
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [validateForm, values, onSubmit]);
  
  return {
    values,
    errors,
    touched,
    isSubmitting,
    isSubmitted,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    resetForm,
    setValues,
    setErrors
  };
};

export default useForm;
